import React, { useEffect, useRef, useState } from 'react';

import { EyeButton } from '../../TableUIKit';
import { useWaApi } from '../../auth/helper/api/waApi';
import { useUserProfileAccess } from '../../auth/helper/useUserProfileAccess';

const PAGE_SIZES = [10, 20, 50, 100];

const INITIAL_FILTERS = {
  mobileNumber: '',
  wabaId: '',
  templateName: '',
  notificationId: '',
  buttonLabelText: '',
  originalMessageResponseId: '',
};

/**
 * User Interest - the interest WhatsApp users showed by tapping a quick-reply button of a template message that is not an
 * opt-out button (the service records each tap as it arrives). A filter section, a grid and paging; read only. The service sends
 * the mobile number masked and only returns the WABAs the signed-in user may see.
 *
 * Only a SYSTEM_ADMIN / ADMIN gets the eye icon that unmasks a number (each unmasking is one call to the service, which records
 * it in the PII access log before answering); a USER only ever sees the static masked text. The service enforces the same rule -
 * and, for an ADMIN, the same-WABA rule - on every call, so hiding the icon is a convenience, not the check.
 */
const WaUserInterestPage = () => {

  const waApi = useWaApi();
  const { canViewPii } = useUserProfileAccess();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // What the user is typing, versus what the grid was last filtered by: typing alone does not search.
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  // Numbers unmasked inline, by interest id. Kept only until the list changes, so an unmasked number never outlives the rows
  // on screen.
  const [revealed, setRevealed] = useState({});
  const [revealing, setRevealing] = useState({});
  const [revealErrors, setRevealErrors] = useState({});

  // Whatever the grid shows next (page, filter, size, refresh) starts masked again, with no old error either. The counter lets a
  // reveal that was still on its way when the list changed be dropped instead of unmasking a row of the new list.
  const listVersion = useRef(0);
  const maskEverything = () => {
    listVersion.current += 1;
    setRevealed({});
    setRevealErrors({});
  };

  useEffect(() => {
    let cancelled = false;
    const fetchInterests = async () => {
      setLoading(true);
      maskEverything();
      try {
        // Only the filters that were filled in are sent.
        const params = { page, size };
        Object.entries(appliedFilters).forEach(([key, value]) => {
          const trimmed = String(value).trim();
          if (trimmed !== '') {
            params[key] = trimmed;
          }
        });
        const response = await waApi.get(process.env.REACT_APP_GET_WA_USER_INTERESTS_BY_PAGE_ENDPOINT, { params });
        if (cancelled) return;
        const { message, object, httpStatus } = response.data;
        if (httpStatus === 200 && object) {
          maskEverything();
          setRows(object.content ?? []);
          setTotalPages(object.totalPages ?? 0);
          setTotalElements(object.totalElements ?? 0);
          setError(null);
        } else {
          setError(message || 'Unable to load the user interests.');
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || err.message || 'Unable to load the user interests.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchInterests();
    return () => { cancelled = true; };
  }, [waApi, page, size, appliedFilters]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    setPage(0);
    setAppliedFilters({ ...filters });
  };

  const resetFilters = () => {
    setPage(0);
    setFilters(INITIAL_FILTERS);
    setAppliedFilters(INITIAL_FILTERS);
  };

  // One call to the service per unmasking. It checks the role and the WABA, records who/where/what in the PII access log, and
  // only then returns the number. Clicking again masks it (no call).
  const toggleReveal = async (interest) => {
    setRevealErrors((prev) => ({ ...prev, [interest.id]: null }));
    if (revealed[interest.id] !== undefined) {
      setRevealed((prev) => {
        const { [interest.id]: dropped, ...rest } = prev;
        return rest;
      });
      return;
    }
    setRevealing((prev) => ({ ...prev, [interest.id]: true }));
    const version = listVersion.current;
    try {
      const response = await waApi.post(`${process.env.REACT_APP_REVEAL_WA_USER_INTEREST_MOBILE_ENDPOINT}/${interest.id}`);
      if (version === listVersion.current) {
        setRevealed((prev) => ({ ...prev, [interest.id]: response.data?.object?.value }));
      }
    } catch (err) {
      if (version === listVersion.current) {
        setRevealErrors((prev) => ({
          ...prev,
          [interest.id]: err.response?.data?.message || err.message || 'An error occurred while fetching the value.',
        }));
      }
    } finally {
      setRevealing((prev) => ({ ...prev, [interest.id]: false }));
    }
  };

  const formatTime = (value) => (value ? new Date(value).toLocaleString() : 'N/A');
  // Template and notification are empty for an interest whose original message is no longer in the database.
  const orNA = (value) => (value === null || value === undefined || value === '' ? 'N/A' : value);

  return (
    <div className="page-container">
      <h1>User Interest</h1>

      <div className="flex-row filter-section">
        <div className="filter-item">
          <label htmlFor="interest-items-per-page">Items per page: </label>
          <select
            id="interest-items-per-page"
            className="w-auto"
            value={size}
            onChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
          >
            {PAGE_SIZES.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
      </div>

      <h3>Filters</h3>
      <form
        className="flex-row filter-section"
        onSubmit={(e) => { e.preventDefault(); applyFilters(); }}
      >
        <div className="filter-item">
          <label htmlFor="interest-filter-mobile">Mobile Number:</label>
          <input
            id="interest-filter-mobile"
            type="text"
            name="mobileNumber"
            value={filters.mobileNumber}
            onChange={handleInputChange}
            placeholder="e.g. 919876543210"
          />
        </div>
        <div className="filter-item">
          <label htmlFor="interest-filter-waba">Business Account Id:</label>
          <input id="interest-filter-waba" type="text" name="wabaId" value={filters.wabaId} onChange={handleInputChange} />
        </div>
        <div className="filter-item">
          <label htmlFor="interest-filter-template">Template Name:</label>
          <input id="interest-filter-template" type="text" name="templateName" value={filters.templateName} onChange={handleInputChange} />
        </div>
        <div className="filter-item">
          <label htmlFor="interest-filter-notification">Notification Id:</label>
          <input id="interest-filter-notification" type="text" name="notificationId" value={filters.notificationId} onChange={handleInputChange} />
        </div>
        <div className="filter-item">
          <label htmlFor="interest-filter-button">Button Text:</label>
          <input id="interest-filter-button" type="text" name="buttonLabelText" value={filters.buttonLabelText} onChange={handleInputChange} />
        </div>
        <div className="filter-item">
          <label htmlFor="interest-filter-response">Message Response Id:</label>
          <input
            id="interest-filter-response"
            type="text"
            name="originalMessageResponseId"
            value={filters.originalMessageResponseId}
            onChange={handleInputChange}
          />
        </div>

        <button type="submit">Apply Filters</button>
        <button type="button" onClick={resetFilters}>Reset Filters</button>
      </form>

      {error && (
        <div role="alert" style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
          {error}
        </div>
      )}

      <h3>User Interests</h3>

      <div className="table-responsive">
        <table className="notifications-table">
          <thead>
            <tr>
              <th>Id</th>
              <th>Business Account Id</th>
              <th>Mobile Number</th>
              <th>Button Text</th>
              <th>Template Name</th>
              <th>Notification Id</th>
              <th>Message Response Id</th>
              <th>Created At</th>
              <th>Updated At</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((interest) => (
              <tr key={interest.id}>
                <td>{interest.id}</td>
                <td>{interest.wabaId}</td>
                {/* Masked by the service. SYSTEM_ADMIN / ADMIN get the eye icon; a USER gets the masked text and nothing else. */}
                <td>
                  <span className={`pii-cell${revealed[interest.id] !== undefined ? ' pii-cell-open' : ''}`}>
                    <span className="pii-masked">{revealed[interest.id] !== undefined ? revealed[interest.id] : interest.toMobileNumber}</span>
                    {canViewPii && (
                      <EyeButton
                        shown={revealed[interest.id] !== undefined}
                        busy={revealing[interest.id] === true}
                        label="Mobile Number"
                        onClick={() => toggleReveal(interest)}
                      />
                    )}
                  </span>
                  {revealErrors[interest.id] && (
                    <div role="alert" style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
                      {revealErrors[interest.id]}
                    </div>
                  )}
                </td>
                <td>{orNA(interest.buttonLabelText)}</td>
                <td>{orNA(interest.templateName)}</td>
                <td className="audit-mono audit-wrap">{orNA(interest.notificationId)}</td>
                <td className="audit-mono audit-wrap">{orNA(interest.originalMessageResponseId)}</td>
                <td>{formatTime(interest.createdTimestamp)}</td>
                <td>{formatTime(interest.updatedTimestamp)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && rows.length === 0 && !error && (
          <div className="audit-empty">No user interests found.</div>
        )}
        {loading && <div className="audit-empty">Loading...</div>}
      </div>

      <div className="pagination-container">
        <button type="button" onClick={() => setPage((p) => Math.max(p - 1, 0))} disabled={page === 0 || loading}>Previous</button>
        <span>Page {totalPages === 0 ? 0 : page + 1} of {totalPages} ({totalElements} records)</span>
        <button type="button" onClick={() => setPage((p) => p + 1)} disabled={page + 1 >= totalPages || loading}>Next</button>
      </div>
    </div>
  );
};

export default WaUserInterestPage;
